/**
 * API クライアント。
 *
 * **ベースURLは必ず先頭スラッシュの相対パス。** 絶対URLにしてはいけない。
 * SPA と API は Fastify が同一オリジンで配信するので、これで両方に解決される。
 * 絶対URL（http://localhost:8080/...）にすると、Cloudflare トンネル(https)越しに
 * 開いた瞬間に mixed content で全XHRがブロックされる。
 *
 * as-of の knownAt は**ここで一元的に載せる**。呼び出し口ごとに載せる形にすると、
 * 画面が増えたときに載せ忘れが出て、画面ごとに違う時点を見ることになる。
 *
 * 信頼性（SRE 画面側 1・2）:
 * - どの呼び出しも 15 秒で打ち切る（アップロードだけは回線次第なので 120 秒）。
 * - GET は通信エラー・タイムアウト・502/503/504 のとき、間隔を空けて 2 回まで再試行する。
 * - POST は自動で再試行しない。利用者の「もう一度送る」で、**同じ Idempotency-Key** を付けて送り直す
 *   （サーバは同じ利用者・同じキーなら最初の応答を返す＝二重登録にならない）。
 * - 応答の X-App-Version が読み込み時と変わったら「新しい版があります」を出す（appEvents）。
 * - エラー応答の requestId（または X-Request-Id ヘッダ）を ApiError に持たせ、問い合わせ番号として見せる。
 * サーバがまだこれらのヘッダを返さない場合も、何も起きないだけで動く。
 */
import { viewpoint } from "./viewpoint.js";

const BASE = "/api";
const TIMEOUT_MS = 15_000;
const UPLOAD_TIMEOUT_MS = 120_000;
const GET_RETRY_DELAYS = [600, 1800];

/** 利用者向けの表示を決めるための分類。状態コードそのものは画面に出さない。 */
export type ErrorKind =
  | "network" | "timeout" | "validation" | "forbidden" | "conflict" | "notFound"
  | "unauthenticated" | "totpSetup" | "rateLimited" | "tooLarge" | "unsupported"
  | "server" | "badRequest"
  /** 409 IDEMPOTENCY_IN_PROGRESS: 同じ送信をサーバがまだ処理中。同じキーで少し後に送り直す。 */
  | "inProgress"
  /** 422 IDEMPOTENCY_KEY_REUSED: 同じキーを別の内容に使った（入力の不備ではない）。 */
  | "keyReused";

/** サーバのエラー本文（A2: code・params・fields を足した形。message は日本語の予備）。 */
export interface FieldError { field: string; code: string; params?: Record<string, unknown> }
export interface ErrorBody {
  error?: string; code?: string; message?: string; detail?: unknown; requestId?: string;
  need?: string; issues?: Array<{ path?: Array<string | number>; message?: string }>;
  params?: Record<string, unknown>;
  fields?: FieldError[];
  /** 409 VERSION_CONFLICT のときの、いま保存されている行。 */
  current?: unknown;
}

export class ApiError extends Error {
  readonly requestId: string | null;
  readonly body: ErrorBody;
  /** 429 のとき、次に押せるまでの秒数（Retry-After）。 */
  readonly retryAfter: number | null;
  constructor(
    readonly status: number, readonly code: string, readonly detail?: unknown,
    extra: { requestId?: string | null; body?: ErrorBody; retryAfter?: number | null } = {},
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
    this.requestId = extra.requestId ?? null;
    this.body = extra.body ?? {};
    this.retryAfter = extra.retryAfter ?? null;
  }
  /** 権限不足（サーバ側の guard が拒否した）。403 全般はボタンを隠す側に倒す（M-04）。操作を隠したうえで理由も出す。 */
  get forbidden(): boolean { return this.status === 403 && this.code !== "TOTP_SETUP_REQUIRED"; }
  get kind(): ErrorKind {
    if (this.code === "NETWORK") return "network";
    if (this.code === "TIMEOUT") return "timeout";
    if (this.code === "TOTP_SETUP_REQUIRED") return "totpSetup";
    if (this.code === "IDEMPOTENCY_IN_PROGRESS") return "inProgress";
    if (this.code === "IDEMPOTENCY_KEY_REUSED") return "keyReused";
    switch (this.status) {
      case 401: return "unauthenticated";
      case 403: return "forbidden";
      case 404: return "notFound";
      case 409: return "conflict";
      case 413: return "tooLarge";
      case 415: return "unsupported";
      case 422: return "validation";
      case 429: return "rateLimited";
    }
    if (this.status >= 500) return "server";
    if (this.status === 400 && ["INVALID_BODY", "INVALID_PAYLOAD", "SCHEMA_VALIDATION_FAILED"].includes(this.code)) return "validation";
    return "badRequest";
  }
  /**
   * 同じ内容をもう一度送ってよい失敗か（届いたか分からない／一時的な失敗）。
   * M4: 処理中（409 IDEMPOTENCY_IN_PROGRESS）も含める。ここでキーを捨てると、最初の処理が
   * 終わった後の送り直しが新しいキーになり、二重に登録される。
   */
  get retryable(): boolean {
    const k = this.kind;
    return k === "network" || k === "timeout" || k === "server" || k === "rateLimited" || k === "inProgress";
  }
}

// ── アプリ全体へ知らせるイベント（401・TOTP 未登録・新しい版） ──────────
/**
 * UX H-02: 401（セッション切れ）はどの画面のどの呼び出しで起きても同じ扱いにしたい。
 * 呼び出し元ごとに catch して判定させると、catch を書き忘れた画面では
 * 「ログインが切れた」のではなく「保存できませんでした」と誤って出続ける。
 * QA-002: TOTP 未登録のローカル利用者はサーバがどの API でも 403 TOTP_SETUP_REQUIRED を返す
 * （/auth/me 等の最小限を除く）。画面側はこれを受けて登録画面に強制的に誘導する。
 */
type AuthEvent = "sessionExpired" | "totpSetupRequired";
type AppEvent = AuthEvent | "newVersion";
const listeners = new Set<(ev: AppEvent) => void>();
export function onAppEvent(fn: (ev: AppEvent) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
export function onAuthEvent(fn: (ev: AuthEvent) => void): () => void {
  return onAppEvent((ev) => { if (ev !== "newVersion") fn(ev); });
}
function emit(ev: AppEvent): void {
  for (const fn of listeners) fn(ev);
}

// ── 版の食い違い（SRE 画面側 2） ──────────────────────────────────────
let firstVersion: string | null = null;
let newVersionSeen = false;
export function appVersion(): string | null { return firstVersion; }
function noticeVersion(res: Response): void {
  const v = res.headers.get("x-app-version");
  if (!v) return;
  if (firstVersion === null) { firstVersion = v; return; }
  if (v !== firstVersion && !newVersionSeen) { newVersionSeen = true; emit("newVersion"); }
}
/** 分割した画面の読み込み失敗（デプロイで古いファイルが消えた）も同じ案内にする。 */
export function announceNewVersion(): void {
  if (!newVersionSeen) { newVersionSeen = true; emit("newVersion"); }
}

/** 応答が knownAt を返してきたら、固定されていない場合に限り固定する。 */
function pinFromResponse(body: unknown): void {
  if (body && typeof body === "object" && "knownAt" in body) {
    const k = (body as { knownAt?: unknown }).knownAt;
    if (typeof k === "string") viewpoint.pinIfUnset(k);
  }
}

/** 固定中の knownAt をクエリに載せる。既に指定されていれば触らない。 */
function withViewpoint(path: string): string {
  const pinned = viewpoint.knownAt;
  if (!pinned) return path;
  const [p, q = ""] = path.split("?");
  const params = new URLSearchParams(q);
  if (!params.has("knownAt")) params.set("knownAt", pinned);
  return `${p}?${params.toString()}`;
}

/**
 * 401・TOTP_SETUP_REQUIRED を共通で検知する。呼び出し元の catch 漏れに依存しない（H-02 / QA-002）。
 *
 * H-1 再発防止: 401 はどれも同じ「ログインが切れました」ではない。
 * サーバはログイン済みのセッションが無効なときだけ `UNAUTHENTICATED` を返す。
 * ログイン失敗（`INVALID_CREDENTIALS`）・TOTP の第2段（`TOTP_REQUIRED`/`TOTP_INVALID`）は
 * いずれも 401 だが、期限切れとは無関係なので、code で区別してから通知する。
 */
function noticeAuthFailure(status: number, code: string): void {
  if (status === 401 && code === "UNAUTHENTICATED") emit("sessionExpired");
  else if (status === 403 && code === "TOTP_SETUP_REQUIRED") emit("totpSetupRequired");
}

/** UUID v4。crypto.randomUUID が無い環境（非セキュアコンテキスト）でも作れるように。 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export interface RequestOptions {
  /** GET のとき、固定中の知識時点を載せずに「いま」を読む（ホーム・準備度は常に最新）。 */
  latest?: boolean;
  /** 作成系 POST の二重送信防止キー。「もう一度送る」では同じ値を渡す。省略時は毎回新しく作る。 */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: ErrorBody = {};
  try { body = await res.json(); } catch { /* 本文が無い場合もある */ }
  const code = body.error ?? String(res.status);
  noticeAuthFailure(res.status, code);
  const ra = Number(res.headers.get("retry-after"));
  // サーバの message（人が読む理由）を detail に載せる。無ければ detail（検証エラーの配列など）
  return new ApiError(res.status, code, body.message ?? body.detail, {
    requestId: body.requestId ?? res.headers.get("x-request-id"),
    body,
    retryAfter: Number.isFinite(ra) && ra > 0 ? ra : null,
  });
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number, outer?: AbortSignal): Promise<Response> {
  const ctl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; ctl.abort(); }, timeoutMs);
  const onOuter = () => ctl.abort();
  outer?.addEventListener("abort", onOuter, { once: true });
  try {
    return await fetch(url, { ...init, signal: ctl.signal });
  } catch (err) {
    if (outer?.aborted) throw err;   // 呼び出し元の中断はそのまま返す
    throw new ApiError(0, timedOut ? "TIMEOUT" : "NETWORK");
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", onOuter);
  }
}

async function request<T>(path: string, init: RequestInit & { idempotencyKey?: string }, opts: { timeoutMs?: number; retries?: number[]; signal?: AbortSignal } = {}): Promise<T> {
  const { idempotencyKey, ...rest } = init;
  // 本文が無い要求（GET・DELETE）に content-type: json を付けると、サーバが空の JSON として拒む
  const headers: Record<string, string> = { ...(typeof rest.body === "string" ? { "content-type": "application/json" } : {}) };
  if (idempotencyKey) headers["idempotency-key"] = idempotencyKey;
  const delays = opts.retries ?? [];
  for (let attempt = 0; ; attempt++) {
    let res: Response;
    try {
      res = await fetchWithTimeout(`${BASE}${path}`, { credentials: "same-origin", ...rest, headers }, opts.timeoutMs ?? TIMEOUT_MS, opts.signal);
    } catch (err) {
      const canRetry = err instanceof ApiError && attempt < delays.length && navigator.onLine !== false;
      if (!canRetry) throw err;
      await sleep(delays[attempt]!);
      continue;
    }
    noticeVersion(res);
    if (!res.ok) {
      if ([502, 503, 504].includes(res.status) && attempt < delays.length) { await sleep(delays[attempt]!); continue; }
      throw await toApiError(res);
    }
    if (res.status === 204) return undefined as T;
    const json = (await res.json()) as T;
    pinFromResponse(json);
    return json;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export const api = {
  /** GET。固定中の知識時点を自動で載せる。通信エラー時は 2 回まで再試行する。 */
  get: <T>(path: string, opts: RequestOptions = {}) =>
    request<T>(opts.latest ? path : withViewpoint(path), { method: "GET" }, { retries: GET_RETRY_DELAYS, signal: opts.signal }),
  /**
   * 書き込みは「いま」に対して行うので knownAt は載せない。
   * **成功したら知識時点の固定を外す**（UX H-01）。保存・取込・報告・紐づけの直後に
   * 呼び出し元が一覧を読み直すとき、固定されたままの古い knownAt のせいで
   * 自分が今書いた内容が一覧・件数に出ない、という事故を防ぐ。
   * 利用者が明示した as-of（台帳の「この日時点」等、knownAt とは別の入力欄）はここでは触らない。
   */
  post: async <T>(path: string, body: unknown, opts: RequestOptions = {}): Promise<T> => {
    const r = await request<T>(path, {
      method: "POST", body: JSON.stringify(body),
      idempotencyKey: opts.idempotencyKey ?? newIdempotencyKey(),
    }, { signal: opts.signal });
    viewpoint.resetKnownAt();
    return r;
  },
  /** 削除・取り消し（自動で再試行しない）。成功したら知識時点の固定を外す。 */
  delete: async <T>(path: string, opts: RequestOptions = {}): Promise<T> => {
    const r = await request<T>(path, { method: "DELETE" }, { signal: opts.signal });
    viewpoint.resetKnownAt();
    return r;
  },
  /** multipart のアップロード。content-type はブラウザに境界を付けさせる。 */
  upload: async <T>(path: string, form: FormData, opts: RequestOptions = {}): Promise<T> => {
    const r = await request<T>(path, {
      method: "POST", body: form,
      idempotencyKey: opts.idempotencyKey ?? newIdempotencyKey(),
    }, { timeoutMs: UPLOAD_TIMEOUT_MS, signal: opts.signal });
    viewpoint.resetKnownAt();
    return r;
  },
  /** ダウンロード用の URL（同一オリジン。Cookie はブラウザが付ける）。 */
  url: (path: string) => `${BASE}${path}`,
};
